import React, { createContext, useState, useContext, useCallback, useEffect, useRef } from 'react';
import { getAutomaticNoteType } from '../utils/automaticNoteType';

// Create the context
const NoteContext = createContext();

// Custom hook to use the NoteContext
export const useNote = () => useContext(NoteContext);

// Provider component that will wrap around the app
export const NoteProvider = ({ children }) => {
  const [latestNote, setLatestNote] = useState(null);
  const [loadingNotes, setLoadingNotes] = useState([]);
  const [lastRecordingId, setLastRecordingId] = useState(null);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isRecordingActive, setIsRecordingActive] = useState(false);
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [allNotes, setAllNotes] = useState([]);
  // Track which notes have been received for each recording session
  const [receivedNotes, setReceivedNotes] = useState({});
  const loadingTimers = useRef(new Map());

  useEffect(() => {
    const timers = loadingTimers.current;
    return () => {
      timers.forEach(clearTimeout);
      timers.clear();
    };
  }, []);

  useEffect(() => {
    const completed = Object.keys(receivedNotes).filter(id =>
      receivedNotes[id].Transcript && receivedNotes[id]['Patient Note']
    );
    if (!completed.length) return;
    if (!isRecording && completed.length === Object.keys(receivedNotes).length) {
      setIsSessionActive(false);
    }
    setLoadingNotes(prev => prev.some(note => completed.includes(note.sessionId))
      ? prev.filter(note => !completed.includes(note.sessionId)) : prev);
    completed.forEach(id => {
      clearTimeout(loadingTimers.current.get(id));
      loadingTimers.current.delete(id);
    });
  }, [receivedNotes, isRecording]);

  const hasPendingSession = useCallback((sessionId) => {
    if (!sessionId) return false;
    const status = receivedNotes[sessionId];
    if (!status) return false;
    return !(status.Transcript && status['Patient Note']);
  }, [receivedNotes]);

  const hasAnyPendingNotes = useCallback(() => {
    return Object.values(receivedNotes).some((status) => !(status?.Transcript && status?.['Patient Note']));
  }, [receivedNotes]);

  // Add a loading note entry when requested
  const addLoadingNote = useCallback((providedSessionId = null) => {
    // Use provided session ID from backend, or create fallback ID
    const sessionId = providedSessionId || crypto.randomUUID();
    console.log('🟢 FRONTEND: Using sessionId for loading notes:', sessionId, providedSessionId ? '(from backend)' : '(fallback)');
    
    // Create two loading note entries (one for transcript, one for patient note)
    const newLoadingNotes = [
      { 
        id: `${sessionId}-transcript`, 
        sessionId: sessionId,
        type: 'Transcript', 
        timestamp: new Date().toISOString() 
      },
      { 
        id: `${sessionId}-note`, 
        sessionId: sessionId,
        type: 'Patient Note', 
        timestamp: new Date().toISOString() 
      }
    ];
    
    setLoadingNotes(prev => [...prev, ...newLoadingNotes]);
    setLastRecordingId(sessionId);
    
    // Initialize tracking for this recording session
    setReceivedNotes(prev => ({
      ...prev,
      [sessionId]: { 
        Transcript: false,
        'Patient Note': false
      }
    }));
    
    // Automatically remove loading notes after 60 seconds if they haven't been removed yet
    clearTimeout(loadingTimers.current.get(sessionId));
    loadingTimers.current.set(sessionId, setTimeout(() => {
      loadingTimers.current.delete(sessionId);
      // Only the placeholder expires, not the pending generation.
      setLoadingNotes(prev => prev.some(note => note.sessionId === sessionId)
        ? prev.filter(note => note.sessionId !== sessionId) : prev);
    }, 60000));
    
    return sessionId;
  }, []);

  // Mark a note type as received for a specific session
  const markNoteReceived = useCallback((sessionId, type) => {
    if (!sessionId || (type !== 'Transcript' && type !== 'Patient Note')) return;

    setReceivedNotes(prev => {
      if (!prev[sessionId] || prev[sessionId][type]) {
        return prev;
      }
      
      // Update the existing tracking
      return {
        ...prev,
        [sessionId]: {
          ...prev[sessionId],
          [type]: true
        }
      };
    });
    
  }, []);
  
  // Remove loading note by session ID and type - now marks it as received but doesn't remove it yet
  const removeLoadingNoteBySessionId = useCallback((sessionId, type) => {
    if (!sessionId) return;
    
    // Mark this note type as received for this session
    markNoteReceived(sessionId, type);
  }, [markNoteReceived]);

  const syncLoadingStateFromSessionNotes = useCallback((sessionId, sessionNotes) => {
    if (!sessionId || !Array.isArray(sessionNotes)) return;
    const hasTranscript = sessionNotes.some((n) => getAutomaticNoteType(n) === 'Transcript');
    const hasPatientNote = sessionNotes.some((n) => getAutomaticNoteType(n) === 'Patient Note');
    setReceivedNotes(prev => {
      const status = prev[sessionId];
      // Missing historical/deleted documents are not pending generation.
      if (!status) return prev;
      const Transcript = status.Transcript || hasTranscript;
      const patientNote = status['Patient Note'] || hasPatientNote;
      if (status.Transcript === Transcript && status['Patient Note'] === patientNote) return prev;
      return { ...prev, [sessionId]: { Transcript, 'Patient Note': patientNote } };
    });
  }, []);

  // For backwards compatibility - remove oldest loading note of specified type
  const removeLoadingNote = useCallback((type) => {
    if (lastRecordingId) {
      // Try to mark note as received for the last session first
      markNoteReceived(lastRecordingId, type);
    } else {
      // Fall back to original behavior if no sessionId is available
      setLoadingNotes(prev => {
        // Find the oldest loading note of the specified type
        const oldestLoadingNote = [...prev]
          .filter(note => note.type === type)
          .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))[0];
        
        // If found, remove it from the loading notes
        if (oldestLoadingNote) {
          return prev.filter(note => note.id !== oldestLoadingNote.id);
        }
        
        return prev;
      });
    }
  }, [lastRecordingId, markNoteReceived]);

  // Enhanced setLatestNote to handle session ID
  const enhancedSetLatestNote = useCallback((note, sessionId) => {
    setLatestNote(note);

    if (sessionId) {
      setCurrentSessionId(sessionId);
      markNoteReceived(sessionId, getAutomaticNoteType(typeof note === 'string' ? { note } : note));
    }
  }, [markNoteReceived]);

  // Function to set note with session context (for when clicking from TopLeftNoteViewer)
  const setNoteWithSession = useCallback((note, sessionId) => {
    setLatestNote(note);
    setCurrentSessionId(sessionId);
  }, []);

  return (
    <NoteContext.Provider value={{ 
      latestNote, 
      setLatestNote: enhancedSetLatestNote,
      loadingNotes,
      addLoadingNote,
      removeLoadingNote,
      removeLoadingNoteBySessionId,
      syncLoadingStateFromSessionNotes,
      lastRecordingId,
      currentSessionId,
      setCurrentSessionId,
      allNotes,
      setAllNotes,
      setNoteWithSession,
      isRecording,
      setIsRecording,
      isRecordingActive,
      setIsRecordingActive,
      isSessionActive,
      setIsSessionActive,
      hasPendingSession,
      hasAnyPendingNotes,
    }}>
      {children}
    </NoteContext.Provider>
  );
};
